import React, { useState, useEffect } from "react";
import { Modal, Button, Input, Select } from "../../components/UI";
import { usersAPI, pipelineAPI } from "../../services/api";
import { useOrg } from "../../contexts/OrgContext";
import toast from "react-hot-toast";
import {
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineExclamationCircle,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineMail,
  HiOutlineShieldCheck,
  HiOutlineViewGrid,
  HiOutlineAdjustments,
  HiOutlineInformationCircle,
  HiCheck,
  HiInformationCircle,
  HiOutlineLocationMarker,
} from "react-icons/hi";

const DEFAULT_VISIT_PERMISSIONS = { canScheduleOwn: true, gpsRequired: false };

const VISIT_PERMISSION_OPTIONS = [
  {
    key: "canScheduleOwn",
    title: "Can schedule own visits",
    description: "Lets this user book site visits / meetings for their own leads",
  },
  {
    key: "gpsRequired",
    title: "GPS required at Start / End",
    description: "The app captures location on check-in and check-out; the visit can't start without it",
  },
];

const roleOptions = [
  { value: "telecaller", label: "Telecaller" },
  // { value: 'manager', label: 'Manager' },
  { value: "admin", label: "Admin" },
  // { value: 'super_admin', label: 'Super Admin' },
];

const phoneLabelOptions = [
  { value: "primary", label: "Primary" },
  { value: "mobile", label: "Mobile" },
  { value: "home", label: "Home" },
  { value: "work", label: "Work" },
  { value: "other", label: "Other" },
];

// Validate phone number (international support: 7-15 digits with optional + prefix)
const validatePhoneNumber = (phone) => {
  if (!phone) return { isValid: false, error: "Phone number is required" };

  // Clean: remove spaces, hyphens, parentheses, dots, brackets
  let cleanPhone = phone.toString().replace(/[\s\-().[\]{}]/g, '');

  const hasPlus = cleanPhone.startsWith('+');

  if (hasPlus) {
    // International format: keep + and digits only
    cleanPhone = '+' + cleanPhone.substring(1).replace(/[^\d]/g, '');
  } else {
    // Remove any remaining non-digit characters
    cleanPhone = cleanPhone.replace(/[^\d]/g, '');
  }

  if (!cleanPhone || cleanPhone === '+') {
    return { isValid: false, error: "Phone number is required" };
  }

  // Validate digit count
  const digitsOnly = hasPlus ? cleanPhone.substring(1) : cleanPhone;
  if (digitsOnly.length < 7 || digitsOnly.length > 15) {
    return { isValid: false, error: "Phone must be 7-15 digits" };
  }

  return { isValid: true, cleanPhone };
};

// Validate email
const EMAIL_MAX_LENGTH = 254;

const validateEmail = (email) => {
  if (!email) return { isValid: false, error: "Email is required" };
  const cleanEmail = email.trim().toLowerCase();
  if (cleanEmail.length > EMAIL_MAX_LENGTH) {
    return { isValid: false, error: `Email must be ${EMAIL_MAX_LENGTH} characters or less` };
  }
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
    return { isValid: false, error: "Invalid email format" };
  }
  return { isValid: true, cleanEmail };
};

// Validate name
const validateName = (name, fieldName = "Name") => {
  if (!name) return { isValid: false, error: `${fieldName} is required` };
  const cleanName = name.trim();
  if (cleanName.length < 2)
    return {
      isValid: false,
      error: `${fieldName} must be at least 2 characters`,
    };
  if (cleanName.length > 50)
    return {
      isValid: false,
      error: `${fieldName} must be 50 characters or less`,
    };
  if (/\d/.test(cleanName))
    return { isValid: false, error: `${fieldName} cannot contain numbers` };
  if (!/^[a-zA-Z\s]+$/.test(cleanName))
    return {
      isValid: false,
      error: `${fieldName} can only contain letters and spaces`,
    };
  return { isValid: true, cleanName };
};

const UserModal = ({ isOpen, onClose, user, onSuccess }) => {
  const { refreshOrg } = useOrg();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phones: [{ number: "", label: "primary", isDefault: true }],
    password: "",
    role: "telecaller",
    // department: '',
    // designation: '',
    dailyCallTarget: 0,
    restrictStages: false,
    allowedStages: [],
    visitPermissions: { ...DEFAULT_VISIT_PERMISSIONS },
    isActive: true,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Email OTP verification state (only for new users)
  const [emailVerified, setEmailVerified] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  const isEditing = !!user;

  // Pipeline stages for the stage-access permission section
  const [pipelineStages, setPipelineStages] = useState([]);
  const [stagesLoading, setStagesLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setStagesLoading(true);
    pipelineAPI
      .getAll()
      .then((res) => {
        if (cancelled) return;
        const stages = (res.data?.data || [])
          .filter((s) => s.isActive !== false)
          .sort((a, b) => a.order - b.order);
        setPipelineStages(stages);
      })
      .catch(() => {
        if (!cancelled) setPipelineStages([]);
      })
      .finally(() => {
        if (!cancelled) setStagesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const clearStageError = () => {
    if (errors.allowedStages) {
      setErrors((prev) => ({ ...prev, allowedStages: "" }));
    }
  };

  const toggleStage = (key) => {
    setFormData((prev) => ({
      ...prev,
      allowedStages: prev.allowedStages.includes(key)
        ? prev.allowedStages.filter((k) => k !== key)
        : [...prev.allowedStages, key],
    }));
    clearStageError();
  };

  const setAllowedStages = (keys) => {
    setFormData((prev) => ({ ...prev, allowedStages: keys }));
    clearStageError();
  };

  const setStageAccessMode = (restricted) => {
    setFormData((prev) => ({
      ...prev,
      restrictStages: restricted,
      // Switching to "selected stages" for the first time starts with all ticked
      allowedStages:
        restricted && prev.allowedStages.length === 0
          ? pipelineStages.map((s) => s.key)
          : prev.allowedStages,
    }));
    clearStageError();
  };

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        email: user.email || "",
        phones:
          user.phones?.length > 0
            ? user.phones
            : [{ number: "", label: "primary", isDefault: true }],
        password: "", // Always start with empty password when editing
        role: user.role || "telecaller",
        // department: user.department || '',
        // designation: user.designation || '',
        dailyCallTarget: user.dailyCallTarget || 0,
        restrictStages: user.restrictStages === true,
        allowedStages: Array.isArray(user.allowedStages) ? user.allowedStages : [],
        visitPermissions: {
          canScheduleOwn: user.visitPermissions?.canScheduleOwn !== false,
          gpsRequired: user.visitPermissions?.gpsRequired === true,
        },
        isActive: user.isActive !== false, // Handle undefined/null as true
      });
    } else {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phones: [{ number: "", label: "primary", isDefault: true }],
        password: "",
        role: "telecaller",
        // department: '',
        // designation: '',
        dailyCallTarget: 0,
        restrictStages: false,
        allowedStages: [],
        visitPermissions: { ...DEFAULT_VISIT_PERMISSIONS },
        isActive: true,
      });
    }
    setErrors({});
    // Reset email verification state when modal opens
    setEmailVerified(false);
    setOtp('');
    setOtpError('');
    setShowOtpModal(false);
  }, [user, isOpen]);

  // Resend OTP timer countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Send OTP to user's email
  const handleSendOtp = async () => {
    // Validate email first
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      setErrors((prev) => ({ ...prev, email: emailValidation.error }));
      return;
    }

    setOtpSending(true);
    setOtpError('');
    try {
      const res = await usersAPI.sendEmailOTP(formData.email.trim().toLowerCase());
      if (res.data?.success) {
        setShowOtpModal(true);
        setOtp('');
        setResendTimer(60);
        toast.success('OTP sent to the email address');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to send OTP. Please try again.';
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setOtpSending(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter the 6-digit OTP');
      return;
    }

    setOtpVerifying(true);
    setOtpError('');
    try {
      const res = await usersAPI.verifyEmailOTP(formData.email.trim().toLowerCase(), otp);
      if (res.data?.success) {
        setEmailVerified(true);
        setShowOtpModal(false);
        setErrors((prev) => ({ ...prev, email: '' }));
        toast.success('Email verified successfully!');
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Invalid OTP. Please try again.';
      setOtpError(msg);
      toast.error(msg);
    } finally {
      setOtpVerifying(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    // Validate first name
    const firstNameValidation = validateName(formData.firstName, "First name");
    if (!firstNameValidation.isValid) {
      newErrors.firstName = firstNameValidation.error;
    }

    // Validate last name
    const lastNameValidation = validateName(formData.lastName, "Last name");
    if (!lastNameValidation.isValid) {
      newErrors.lastName = lastNameValidation.error;
    }

    // Validate email
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      newErrors.email = emailValidation.error;
    }

    // Validate password (required for new users)
    if (!isEditing && !formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password && formData.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    } else if (formData.password && formData.password.length > 128) {
      newErrors.password = "Password must be 128 characters or less";
    }

    // Validate phone numbers
    const validPhones = formData.phones.filter(
      (p) => p.number && p.number.trim(),
    );
    if (validPhones.length === 0) {
      newErrors.phones = "At least one phone number is required";
    } else {
      const phoneErrors = [];
      for (const phone of validPhones) {
        const validation = validatePhoneNumber(phone.number);
        if (!validation.isValid) {
          phoneErrors.push(validation.error);
        }
      }
      if (phoneErrors.length > 0) {
        newErrors.phones = phoneErrors[0]; // Show first error
      }
    }

    // Stage access: at least one stage when restricted (admins always see all)
    if (
      formData.role !== "admin" &&
      formData.restrictStages &&
      formData.allowedStages.length === 0
    ) {
      newErrors.allowedStages = "Select at least one stage";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Prevent numbers in name fields
    if ((name === "firstName" || name === "lastName") && /\d/.test(value)) {
      return;
    }

    // Reset email verification when email changes
    if (name === 'email') {
      setEmailVerified(false);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : (name === "dailyCallTarget" ? parseInt(value, 10) || 0 : value),
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePhoneChange = (index, field, value) => {
    // For phone number, allow digits, +, spaces, and hyphens
    if (field === "number") {
      // Allow empty value or valid phone characters
      if (value && !/^[\d\s\-+]*$/.test(value)) {
        return;
      }
      // Limit to reasonable length (including +91 prefix)
      if (value && value.replace(/[\s\-]/g, "").length > 15) {
        return;
      }
    }

    setFormData((prev) => {
      const newPhones = [...prev.phones];
      newPhones[index] = { ...newPhones[index], [field]: value };

      // If setting a phone as default, remove default from others
      if (field === "isDefault" && value === true) {
        newPhones.forEach((phone, i) => {
          if (i !== index) {
            newPhones[i] = { ...phone, isDefault: false };
          }
        });
        // Also set label to primary for default
        newPhones[index].label = "primary";
      }

      return { ...prev, phones: newPhones };
    });
    if (errors.phones) {
      setErrors((prev) => ({ ...prev, phones: "" }));
    }
  };

  const addPhone = () => {
    setFormData((prev) => ({
      ...prev,
      phones: [
        ...prev.phones,
        { number: "", label: "secondary", isDefault: false },
      ],
    }));
  };

  const removePhone = (index) => {
    if (formData.phones.length === 1) {
      toast.error("At least one phone number is required");
      return;
    }
    setFormData((prev) => ({
      ...prev,
      phones: prev.phones.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    // Email verification is required for new users
    if (!isEditing && !emailVerified) {
      setErrors((prev) => ({ ...prev, email: 'Please verify the email address before creating the user' }));
      return;
    }

    setIsSubmitting(true);
    try {
      const data = { ...formData };

      // Clean and format phone numbers
      const cleanedPhones = data.phones
        .filter((p) => p.number && p.number.trim())
        .map((p) => {
          // Validate and clean phone number
          const validation = validatePhoneNumber(p.number);
          return {
            ...p,
            number: validation.isValid
              ? validation.cleanPhone
              : p.number.replace(/[\s\-]/g, ""),
          };
        });

      if (cleanedPhones.length === 0) {
        setErrors({ phones: "At least one phone number is required" });
        setIsSubmitting(false);
        return;
      }

      // Ensure at least one phone is default
      if (!cleanedPhones.some((p) => p.isDefault)) {
        cleanedPhones[0].isDefault = true;
        cleanedPhones[0].label = "primary";
      }

      data.phones = cleanedPhones;

      // Clean email to lowercase
      data.email = data.email.trim().toLowerCase();

      // Don't send empty password for editing
      if (isEditing && !data.password) {
        delete data.password;
      }

      // Ensure isActive is properly set as boolean
      data.isActive = data.isActive === true || data.isActive === "true";

      if (isEditing) {
        await usersAPI.update(user._id, data);
        toast.success("User updated successfully");
      } else {
        data.emailVerified = emailVerified;
        const createRes = await usersAPI.create(data);
        const body = createRes?.data || {};
        toast.success(body.message || "User created successfully");
        if (body.emailWarning) {
          toast.error(body.emailWarning, { duration: 8000 });
        }
      }
      onSuccess();
      onClose();
    } catch (error) {
      const responseData = error.response?.data;
      const message = responseData?.message || "Failed to save user";

      if (responseData?.code === 'LIMIT_EXCEEDED') {
        // User limit exceeded — show clear message and refresh org data
        toast.error(message, { duration: 5000 });
        refreshOrg();
      } else if (responseData?.code === 'EMAIL_NOT_VERIFIED') {
        // Email not verified — reset verification state and show error
        setEmailVerified(false);
        setErrors((prev) => ({ ...prev, email: message }));
        toast.error(message);
      } else if (responseData?.errors) {
        // Handle validation errors from backend
        const backendErrors = responseData.errors;
        setErrors((prev) => ({ ...prev, ...backendErrors }));
      } else {
        toast.error(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? "Edit User" : "Add New User"}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Input
              label={
                <>
                  First Name <span className="text-red-500">*</span>
                </>
              }
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder="Enter first name"
              error={errors.firstName}
              maxLength={50}
              onInput={(e) => {
                e.target.value = e.target.value.replace(/\d/g, "");
              }}
            />
            <p className={`text-xs text-right mt-0.5 ${formData.firstName.length > 50 ? 'text-red-500' : 'text-gray-400'}`}>
              {formData.firstName.length}/50
            </p>
          </div>
          <div>
            <Input
              label={
                <>
                  Last Name <span className="text-red-500">*</span>
                </>
              }
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder="Enter last name"
              error={errors.lastName}
              maxLength={50}
              onInput={(e) =>
                (e.target.value = e.target.value.replace(/\d/g, ""))
              }
            />
            <p className={`text-xs text-right mt-0.5 ${formData.lastName.length > 50 ? 'text-red-500' : 'text-gray-400'}`}>
              {formData.lastName.length}/50
            </p>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 min-w-0">
          {!isEditing ? (
            <div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Input
                    label={
                      <>
                        Email <span className="text-red-500">*</span>
                      </>
                    }
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    error={errors.email}
                    disabled={isSubmitting}
                    maxLength={254}
                  />
                </div>
                <button
                  type="button"
                  onClick={handleSendOtp}
                  disabled={otpSending || !formData.email.trim() || emailVerified}
                  className="mb-0.5 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all whitespace-nowrap shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: emailVerified ? '#10b981' : '#4f46e5',
                    color: 'white',
                    border: 'none',
                    height: '46px',
                  }}
                >
                  {emailVerified ? (
                    <span className="flex items-center gap-1">
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Verified
                    </span>
                  ) : otpSending ? (
                    'Sending...'
                  ) : resendTimer > 0 ? (
                    `Resend (${resendTimer}s)`
                  ) : (
                    'Verify Email'
                  )}
                </button>
              </div>
              <p className={`text-xs text-right mt-0.5 ${formData.email.length > 254 ? 'text-red-500' : 'text-gray-400'}`}>
                {formData.email.length}/254
              </p>
              {emailVerified && (
                <p className="mt-1 text-xs text-green-600 font-medium flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  Email verified successfully
                </p>
              )}
            </div>
          ) : (
            <div>
              <Input
                label={
                  <>
                    Email <span className="text-red-500">*</span>
                  </>
                }
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                error={errors.email}
                disabled={isEditing}
                maxLength={254}
              />
              <p className="text-xs text-gray-500 mt-1">
                Email cannot be changed after creation
              </p>
            </div>
          )}
        </div>

          {/* Active / inactive status toggle */}
          <div className="sm:w-44 flex-shrink-0 space-y-1.5">
            <label className="block text-sm font-semibold text-secondary-700">
              Status
            </label>
            <button
              type="button"
              role="switch"
              aria-checked={formData.isActive}
              aria-label="Active user"
              onClick={() =>
                setFormData((prev) => ({ ...prev, isActive: !prev.isActive }))
              }
              className={`w-full h-[46px] flex items-center justify-between gap-3 px-3.5 rounded-xl border-2 transition-all duration-200 focus:outline-none focus:ring-4 focus:ring-primary-500/10 ${
                formData.isActive
                  ? "border-green-200 bg-green-50"
                  : "border-secondary-200 bg-secondary-50"
              }`}
            >
              <span
                className={`flex items-center gap-2 text-sm font-semibold ${
                  formData.isActive ? "text-green-700" : "text-secondary-500"
                }`}
              >
                <span
                  className={`w-2 h-2 rounded-full ${
                    formData.isActive ? "bg-green-500" : "bg-secondary-400"
                  }`}
                />
                {formData.isActive ? "Active" : "Inactive"}
              </span>
              <span
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 ${
                  formData.isActive ? "bg-green-500" : "bg-secondary-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${
                    formData.isActive ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </span>
            </button>
          </div>
        </div>

        {/* Phone Numbers Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Phone Numbers <span className="text-red-500">*</span>
              </label>
              <p className="text-xs text-gray-500 mt-0.5">
                Enter phone number (7-15 digits, with optional + country code for international numbers)
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              icon={<HiOutlinePlus className="w-4 h-4" />}
              onClick={addPhone}
            >
              Add Phone
            </Button>
          </div>

          {errors.phones && (
            <div className="flex items-center gap-2 p-2 bg-red-50 border border-red-200 rounded-lg">
              <HiOutlineExclamationCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600">{errors.phones}</p>
            </div>
          )}

          <div className="space-y-3">
            {formData.phones.map((phone, index) => (
              <div
                key={index}
                className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-1 grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <Input
                      placeholder="e.g., 9876543210 or +91 9876543210"
                      value={phone.number}
                      onChange={(e) =>
                        handlePhoneChange(index, "number", e.target.value)
                      }
                    />
                  </div>
                  <Select
                    options={phoneLabelOptions}
                    value={phone.label}
                    onChange={(e) =>
                      handlePhoneChange(index, "label", e.target.value)
                    }
                  />
                </div>
                <div className="flex items-center gap-2 pt-2">
                  <label className="flex items-center gap-1 text-xs text-gray-600">
                    <input
                      type="radio"
                      name="defaultPhone"
                      checked={phone.isDefault}
                      onChange={() =>
                        handlePhoneChange(index, "isDefault", true)
                      }
                      className="w-3 h-3 text-primary-600"
                    />
                    Default
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    icon={<HiOutlineTrash className="w-4 h-4 text-red-500" />}
                    onClick={() => removePhone(index)}
                    disabled={formData.phones.length === 1}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <Input
            label={
              isEditing ? (
                "New Password"
              ) : (
                <>
                  Password <span className="text-red-500">*</span>
                </>
              )
            }
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleChange}
            placeholder={
              isEditing
                ? "Leave blank to keep current password"
                : "Enter password (min 6 characters)"
            }
            error={errors.password}
            maxLength={128}
            rightIcon={
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="text-secondary-400 hover:text-secondary-600 focus:outline-none"
                tabIndex={-1}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <HiOutlineEyeOff className="w-5 h-5" />
                ) : (
                  <HiOutlineEye className="w-5 h-5" />
                )}
              </button>
            }
          />
          <p className={`text-xs text-right mt-0.5 ${formData.password.length > 128 ? 'text-red-500' : 'text-gray-400'}`}>
            {formData.password.length}/128
          </p>
          {isEditing && (
            <p className="text-xs text-gray-500 mt-1">
              Leave blank to keep the current password unchanged
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Select
            label={
              <>
                Role <span className="text-red-500">*</span>
              </>
            }
            name="role"
            options={roleOptions}
            value={formData.role}
            onChange={handleChange}
          />
          {/* <Input
            label="Department"
            name="department"
            value={formData.department}
            onChange={handleChange}
            placeholder="e.g., Sales"
          /> */}

          {/* Daily Call Target — only shown for telecaller role */}
          {formData.role === 'telecaller' && (
            <div className="space-y-1.5">
              <div className="flex items-center gap-1.5">
                <label htmlFor="dailyCallTarget" className="block text-sm font-semibold text-secondary-700">
                  Daily Call Target
                </label>
                <div className="relative group flex items-center">
                  <span
                    tabIndex={0}
                    aria-label="About daily call target"
                    aria-describedby="dailyCallTargetInfo"
                    className="flex items-center justify-center rounded-full text-primary-500 group-hover:text-primary-700 transition-colors cursor-help focus:outline-none"
                  >
                    <HiInformationCircle className="w-[18px] h-[18px]" />
                  </span>
                  <div
                    id="dailyCallTargetInfo"
                    role="tooltip"
                    className="pointer-events-none invisible opacity-0 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100 transition-opacity duration-150 absolute z-20 right-0 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 bottom-full mb-2 w-60 px-3 py-2 rounded-lg bg-secondary-800 text-white text-xs font-normal leading-relaxed shadow-lg"
                  >
                    Minimum calls expected per day. Set to 0 for no target. Missed targets trigger email notifications.
                    <span className="absolute top-full right-1.5 sm:right-auto sm:left-1/2 sm:-translate-x-1/2 border-4 border-transparent border-t-secondary-800" />
                  </div>
                </div>
              </div>
              <div className="relative">
                <input
                  type="number"
                  id="dailyCallTarget"
                  name="dailyCallTarget"
                  min="0"
                  value={formData.dailyCallTarget}
                  onChange={handleChange}
                  placeholder="0"
                  className="w-full pl-3.5 pr-24 py-2.5 rounded-xl border-2 border-secondary-200 text-sm bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-500/10 focus:outline-none transition-all duration-200"
                />
                <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-sm text-secondary-400 pointer-events-none">
                  calls / day
                </span>
              </div>
            </div>
          )}
        </div>

        {/* <Input
          label="Designation"
          name="designation"
          value={formData.designation}
          onChange={handleChange}
          placeholder="e.g., Senior Telecaller"
        /> */}

        {/* Stage Access — which pipeline stages this user sees in the app */}
        {formData.role !== "admin" && (() => {
          const selectedCount = pipelineStages.filter((s) =>
            formData.allowedStages.includes(s.key),
          ).length;
          const accessOptions = [
            {
              restricted: false,
              icon: HiOutlineViewGrid,
              title: "All stages",
              description: "Sees every stage, including ones added later",
            },
            {
              restricted: true,
              icon: HiOutlineAdjustments,
              title: "Selected stages",
              description: "Sees only the stages you pick below",
            },
          ];

          return (
            <div className="rounded-xl border border-secondary-200 overflow-hidden">
              {/* Header */}
              <div className="flex items-start justify-between gap-3 px-4 py-3 bg-secondary-50 border-b border-secondary-200">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0">
                    <HiOutlineShieldCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-secondary-800">Stage Access</p>
                    <p className="text-xs text-secondary-500 mt-0.5">
                      Which lead stages this user can see in the app
                    </p>
                  </div>
                </div>
                <span
                  className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${
                    formData.restrictStages
                      ? "bg-amber-100 text-amber-700"
                      : "bg-green-100 text-green-700"
                  }`}
                >
                  {formData.restrictStages
                    ? `${selectedCount} of ${pipelineStages.length} stages`
                    : "Full access"}
                </span>
              </div>

              <div className="p-4 space-y-4">
                {/* Access mode cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {accessOptions.map((opt) => {
                    const active = formData.restrictStages === opt.restricted;
                    const Icon = opt.icon;
                    return (
                      <button
                        key={opt.title}
                        type="button"
                        onClick={() => setStageAccessMode(opt.restricted)}
                        className={`relative text-left p-3 rounded-xl border-2 transition-all duration-200 ${
                          active
                            ? "border-primary-500 bg-primary-50 ring-4 ring-primary-500/10"
                            : "border-secondary-200 bg-white hover:border-secondary-300"
                        }`}
                      >
                        <div className="flex items-start gap-2.5">
                          <Icon
                            className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                              active ? "text-primary-600" : "text-secondary-400"
                            }`}
                          />
                          <div className="pr-5">
                            <p
                              className={`text-sm font-semibold ${
                                active ? "text-primary-700" : "text-secondary-700"
                              }`}
                            >
                              {opt.title}
                            </p>
                            <p className="text-xs text-secondary-500 mt-0.5">
                              {opt.description}
                            </p>
                          </div>
                        </div>
                        <span
                          className={`absolute top-3 right-3 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            active ? "border-primary-600 bg-primary-600" : "border-secondary-300"
                          }`}
                        >
                          {active && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Stage picker */}
                {formData.restrictStages && (
                  <div>
                    {stagesLoading ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {[0, 1, 2, 3].map((i) => (
                          <div key={i} className="h-11 rounded-lg bg-secondary-100 animate-pulse" />
                        ))}
                      </div>
                    ) : pipelineStages.length === 0 ? (
                      <p className="text-sm text-secondary-500 text-center py-4">
                        No pipeline stages found.
                      </p>
                    ) : (
                      <>
                        <div className="flex items-center justify-between mb-2.5">
                          <p className="text-xs font-semibold uppercase tracking-wide text-secondary-500">
                            Visible stages
                          </p>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              className="text-xs font-semibold text-primary-600 hover:bg-primary-50 px-2 py-1 rounded-md transition-colors"
                              onClick={() => setAllowedStages(pipelineStages.map((s) => s.key))}
                            >
                              Select all
                            </button>
                            <span className="text-secondary-300">|</span>
                            <button
                              type="button"
                              className="text-xs font-semibold text-secondary-500 hover:bg-secondary-100 px-2 py-1 rounded-md transition-colors"
                              onClick={() => setAllowedStages([])}
                            >
                              Clear
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-64 overflow-y-auto pr-1">
                          {pipelineStages.map((stage) => {
                            const checked = formData.allowedStages.includes(stage.key);
                            const color = stage.color || "#6B7280";
                            return (
                              <button
                                key={stage._id || stage.key}
                                type="button"
                                role="checkbox"
                                aria-checked={checked}
                                onClick={() => toggleStage(stage.key)}
                                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-all duration-150 ${
                                  checked
                                    ? "shadow-sm"
                                    : "border-secondary-200 bg-white hover:border-secondary-300 opacity-70 hover:opacity-100"
                                }`}
                                style={
                                  checked
                                    ? { borderColor: color, backgroundColor: `${color}14` }
                                    : undefined
                                }
                              >
                                <span
                                  className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 border-2 transition-colors"
                                  style={
                                    checked
                                      ? { backgroundColor: color, borderColor: color }
                                      : { borderColor: "#CBD5E1" }
                                  }
                                >
                                  {checked && <HiCheck className="w-3.5 h-3.5 text-white" />}
                                </span>
                                <span
                                  className={`text-sm truncate ${
                                    checked ? "font-semibold text-secondary-800" : "text-secondary-600"
                                  }`}
                                >
                                  {stage.name}
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        <p className="flex items-start gap-1.5 text-xs text-secondary-500 mt-3">
                          <HiOutlineInformationCircle className="w-4 h-4 flex-shrink-0" />
                          Stages added later stay hidden for this user until you enable them here.
                        </p>
                      </>
                    )}
                    {errors.allowedStages && (
                      <div className="flex items-center gap-2 mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                        <HiOutlineExclamationCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                        <p className="text-sm text-red-600">{errors.allowedStages}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })()}

        {/* Visit permissions — Visits & Meetings module (admins always have full access) */}
        {formData.role !== "admin" && (
          <div className="rounded-xl border border-secondary-200 overflow-hidden">
            <div className="flex items-start gap-3 px-4 py-3 bg-secondary-50 border-b border-secondary-200">
              <div className="w-9 h-9 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center flex-shrink-0">
                <HiOutlineLocationMarker className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-secondary-800">Visit Permissions</p>
                <p className="text-xs text-secondary-500 mt-0.5">
                  Site visits and customer meetings for this user
                </p>
              </div>
            </div>
            <div className="divide-y divide-secondary-100">
              {VISIT_PERMISSION_OPTIONS.map((opt) => {
                const on = formData.visitPermissions?.[opt.key] === true;
                return (
                  <div key={opt.key} className="flex items-center justify-between gap-4 px-4 py-3">
                    <div>
                      <p id={`visit-perm-${opt.key}`} className="text-sm font-medium text-secondary-800">
                        {opt.title}
                      </p>
                      <p className="text-xs text-secondary-500 mt-0.5">{opt.description}</p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={on}
                      aria-labelledby={`visit-perm-${opt.key}`}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          visitPermissions: { ...prev.visitPermissions, [opt.key]: !on },
                        }))
                      }
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors ${
                        on ? "bg-success-500" : "bg-secondary-300"
                      }`}
                    >
                      <span
                        className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                          on ? "translate-x-5" : ""
                        }`}
                      />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex gap-3 justify-end pt-4 border-t border-gray-100">
          <Button variant="ghost" onClick={onClose} type="button">
            Cancel
          </Button>
          <Button variant="primary" type="submit" loading={isSubmitting}>
            {isEditing ? "Update User" : "Create User"}
          </Button>
        </div>
      </form>

      {/* OTP Verification Modal */}
      {showOtpModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 max-w-sm w-full">
            <div className="text-center mb-6">
              <div className="mx-auto w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center mb-4">
                <HiOutlineMail className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Verify Email Address</h3>
              <p className="text-sm text-gray-500 mt-1">
                We sent a 6-digit code to<br />
                <span className="font-semibold text-gray-700">{formData.email}</span>
              </p>
            </div>

            {otpError && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200">
                <p className="text-sm text-red-700 text-center">{otpError}</p>
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Enter OTP</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtp(val);
                    if (otpError) setOtpError('');
                  }}
                  className="w-full text-center text-2xl tracking-[0.5em] font-bold py-3 px-4 border-2 border-gray-200 rounded-xl focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none transition-all"
                  placeholder="000000"
                  autoFocus
                />
              </div>

              <button
                type="button"
                onClick={handleVerifyOtp}
                disabled={otpVerifying || otp.length !== 6}
                className="w-full py-3 px-4 rounded-xl text-white font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#4f46e5' }}
              >
                {otpVerifying ? 'Verifying...' : 'Verify OTP'}
              </button>

              <div className="text-center">
                {resendTimer > 0 ? (
                  <p className="text-sm text-gray-500">
                    Resend OTP in <span className="font-semibold text-gray-700">{resendTimer}s</span>
                  </p>
                ) : (
                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpSending}
                    className="text-sm font-semibold text-primary-600 hover:text-primary-700 disabled:opacity-50"
                  >
                    {otpSending ? 'Sending...' : 'Resend OTP'}
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => { setShowOtpModal(false); setOtp(''); setOtpError(''); }}
                className="w-full py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UserModal;
